import { lazy, Suspense, useEffect, useState } from "react";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { WARNING_COLORS } from "../../../shared/alerts";
import type { WarningShape } from "../../../shared/models";
import { useRadarTimeline } from "../../data/queries";
import { useWarningShapes } from "../../data/weatherQueries";
import { Icon } from "../../ui/Icon";
import { EmptyState } from "../../ui/StatusBadge";
import { relativeAge, useNow } from "../../ui/time";
import { remPx } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { AlertBanner, CurrentRow, useWeatherSummary } from "../weather/WeatherWidget";
import { WidgetHeader } from "../WidgetHeader";
import type { Basemap } from "./RadarMap";

// Leaflet is only downloaded when a radar widget is actually on screen.
const RadarMap = lazy(() => import("./RadarMap").then((m) => ({ default: m.RadarMap })));

const FRAME_MS = 650;
const HOLD_LAST_MS = 2200;

interface RadarConfig {
  zoom: number;
  basemap: Basemap;
  opacity: number;
  animate: boolean;
  showConditions: boolean;
  showWarnings: boolean;
}

function Radar({ config, editing, fullscreen, openFullscreen, size }: WidgetProps<RadarConfig>) {
  const { settings, isAdmin } = useHousehold();
  const { openSettings } = useAppUi();
  const timeline = useRadarTimeline();
  const wx = useWeatherSummary();
  const warnings = useWarningShapes(config.showWarnings);
  const frames = timeline.data?.frames ?? [];
  const [frameIdx, setFrameIdx] = useState(0);
  const [playing, setPlaying] = useState(config.animate);
  const [loadFailed, setLoadFailed] = useState(false);
  useNow("minute");

  // Jump to the newest frame whenever a new timeline arrives.
  const framesKey = frames.join("|");
  useEffect(() => setFrameIdx(Math.max(0, frames.length - 1)), [framesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animate only genuine frames, pausing on the newest; stop in edit mode or when the page is hidden.
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  const animating = playing && visible && !editing && frames.length > 1;
  useEffect(() => {
    if (!animating) return;
    const isLast = frameIdx === frames.length - 1;
    const t = window.setTimeout(() => setFrameIdx((i) => (i + 1) % frames.length), isLast ? HOLD_LAST_MS : FRAME_MS);
    return () => clearTimeout(t);
  }, [animating, frameIdx, frames.length]);

  if (!settings.location) {
    return (
      <EmptyState
        icon="radar"
        title="Radar needs a home location"
        action={
          isAdmin ? (
            <button className="btn btn-sm btn-primary" onClick={() => openSettings("location")}>
              Set location
            </button>
          ) : undefined
        }
      />
    );
  }

  const data = wx.q.data;
  const obs = data?.observation;
  const compact = size.variant === "compact";
  const fmt = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).replace(/ (AM|PM)$/, "");
  const current = frames[frameIdx];
  const rem = remPx();
  // Wide, short cards put the conditions beside the map so the map keeps its height.
  const side = config.showConditions && !compact && !fullscreen && size.width > size.height * 1.45 && size.width > rem * 36;
  const showNow = side || fullscreen || (!compact && size.height > rem * 21);

  return (
    <div className={`radar ${side ? "is-side" : ""}`}>
      {config.showConditions && (
        <div className="radar-cond">
          <WidgetHeader
            title={
              <>
                {data?.location.label || settings.location.label || "Home"} <Icon name="navigate" size="0.95rem" className="title-icon" />
              </>
            }
            meta={
              obs
                ? `Updated ${new Date(obs.observedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
                : data
                  ? `Forecast updated ${relativeAge(data.stamps.forecast?.fetchedAt ?? Date.now())}`
                  : undefined
            }
            onTitle={fullscreen ? undefined : openFullscreen}
          />
          {data && showNow && <CurrentRow data={data} unit={settings.temperatureUnit} />}
          {data && data.alerts.length > 0 && <AlertBanner alerts={data.alerts} />}
        </div>
      )}
      <div className="radar-frame-box">
        <Suspense fallback={<div className="widget-loading" />}>
          <RadarMap
            home={settings.location}
            timeline={timeline.data}
            frameIdx={frameIdx}
            zoom={fullscreen ? Math.min(config.zoom + 0.5, 11) : config.zoom}
            basemap={config.basemap}
            opacity={config.opacity / 100}
            interactive={!editing}
            warnings={config.showWarnings ? warnings.data : undefined}
            onLoadError={setLoadFailed}
          />
        </Suspense>
        {config.showWarnings && warnings.data && <WarningLegend shapes={warnings.data} offset={!config.showConditions && !fullscreen} quiet={size.variant !== "expanded" && !fullscreen} />}
        {!config.showConditions && !fullscreen && (
          <button className="map-btn radar-expand" onClick={openFullscreen} aria-label="Full-screen radar">
            <Icon name="expand" size="1.1rem" />
          </button>
        )}
        {current && (
          <div className="radar-stamp">
            Radar {frameIdx === frames.length - 1 ? `latest · ${relativeAge(current)}` : relativeAge(current)}
          </div>
        )}
        {(timeline.isError && !timeline.data) || loadFailed ? (
          <div className="radar-unavailable">{loadFailed ? "Radar image failed to load." : "NOAA radar is unavailable right now."} Retrying automatically.</div>
        ) : null}
      </div>
      <div className="radar-bar">
        {frames.length > 1 && (
          <button className="icon-btn" onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause radar loop" : "Play radar loop"}>
            <Icon name={playing ? "pause" : "play"} />
          </button>
        )}
        {frames.length > 0 ? (
          <div className="scrub" role="group" aria-label="Radar frames">
            <div className="scrub-track">
              <i className="scrub-fill" style={{ width: `${frames.length > 1 ? (frameIdx / (frames.length - 1)) * 100 : 100}%` }} />
            </div>
            <div className="scrub-ticks">
              {frames.map((f, i) => (
                <button
                  key={f}
                  className={`scrub-tick ${i === frameIdx ? "on" : ""}`}
                  onClick={() => {
                    setPlaying(false);
                    setFrameIdx(i);
                  }}
                  aria-label={`Radar at ${fmt(f)}`}
                >
                  <i />
                  {!compact && <span>{fmt(f)}</span>}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <span className="muted">Radar timeline loading…</span>
        )}
        <span className="radar-legend" aria-label="Precipitation intensity, light to heavy">
          <span>Light</span>
          <i />
          <span>Heavy</span>
        </span>
      </div>
    </div>
  );
}

/** Which storm warnings are outlined on the map, in NWS colors, with counts. */
function WarningLegend({ shapes, offset, quiet }: { shapes: WarningShape[]; offset: boolean; quiet: boolean }) {
  const counts = new Map<string, number>();
  for (const s of shapes) counts.set(s.event, (counts.get(s.event) ?? 0) + 1);
  const rows = [...counts].sort(([a], [b]) => Number(/watch/i.test(a)) - Number(/watch/i.test(b)) || a.localeCompare(b));
  if (rows.length === 0) return quiet ? null : <div className={`warning-legend is-empty ${offset ? "is-offset" : ""}`}>No storm warnings in the state</div>;
  return (
    <ul className={`warning-legend ${offset ? "is-offset" : ""}`} aria-label="Storm warnings outlined on the map">
      {rows.map(([event, n]) => (
        <li key={event}>
          <i className={/watch/i.test(event) ? "is-watch" : ""} style={{ ["--warn-color" as string]: WARNING_COLORS[event] ?? "#fff" }} />
          {event}
          {n > 1 && <b>×{n}</b>}
        </li>
      ))}
    </ul>
  );
}

export const radarWidget: WidgetDefinition<RadarConfig> = {
  type: "radar",
  version: 1,
  name: "Weather & radar",
  description: "NOAA radar loop over a local map, with current conditions across the top.",
  icon: "radar",
  category: "weather",
  defaultSize: { w: 5, h: 6 },
  minSize: { w: 3, h: 3 },
  stackHeight: 520,
  defaults: { zoom: 8, basemap: "topo", opacity: 80, animate: true, showConditions: true, showWarnings: true },
  settings: [
    {
      key: "zoom",
      label: "Zoom",
      type: "select",
      options: [
        { value: 6, label: "Region" },
        { value: 7, label: "Wide" },
        { value: 8, label: "Local" },
        { value: 9, label: "Close" },
      ],
    },
    {
      key: "basemap",
      label: "Map style",
      type: "select",
      options: [
        { value: "topo", label: "Streets & terrain" },
        { value: "relief", label: "Shaded relief" },
        { value: "imagery", label: "Satellite" },
      ],
    },
    { key: "opacity", label: "Radar opacity (%)", type: "number", min: 30, max: 100, step: 5 },
    { key: "animate", label: "Animate radar loop", type: "toggle", help: "Plays real radar frames from about the last hour." },
    { key: "showConditions", label: "Show current conditions", type: "toggle" },
    { key: "showWarnings", label: "Outline storm warnings", type: "toggle", help: "Tornado, severe thunderstorm and flash flood warnings, plus watches, in National Weather Service colors." },
  ],
  provider: "noaa-radar",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: Radar,
};
